import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Zap, CalendarDays, TrendingUp, Menu, X, Home, Wallet, ChevronDown }
  from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import LoginModal from './LoginModal';
import NotificationBell from './NotificationBell';
import UserDropdown from './UserDropdown';

// Shared top navigation so the main app and the Insider section stay consistent:
// the VideBits logo + name always go home, the premium links (Insider, Calendar,
// Optimiser), and the same auth controls (notifications + account, or sign in/up)
// everywhere it's used. Self-contained — it manages its own login modal.
// The /seo/* hubs are server-rendered pages, not SPA routes, so these are plain
// anchors — <Link> would push a client route that does not exist. Releases is
// deliberately absent: /calendar is the native equivalent and already top-level.
const BROWSE_HUBS = [
  // `spa: true` = native route. The /seo version of each stays for crawlers.
  // News and Where to watch are config-driven rather than entity lists, so
  // they are still server-rendered.
  { href: '/seo/news', label: 'News' },
  { href: '/genres', label: 'Genres', spa: true },
  { href: '/seo/watch', label: 'Where to watch' },
  { href: '/studios', label: 'Studios', spa: true },
  { href: '/people', label: 'Cast & crew', spa: true },
  { href: '/franchises', label: 'Franchises', spa: true },
];

export default function SiteHeader() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { pathname } = useLocation();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginModalMode, setLoginModalMode] = useState('login');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [browseOpen, setBrowseOpen] = useState(false);
  const browseRef = useRef(null);

  // Close the Browse dropdown on outside click or Escape — without this it
  // stays open behind whatever the user clicks next.
  useEffect(() => {
    if (!browseOpen) return undefined;
    const onDocClick = (e) => {
      if (browseRef.current && !browseRef.current.contains(e.target)) {
        setBrowseOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setBrowseOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [browseOpen]);

  const openLoginModal = (mode = 'login') => {
    setLoginModalMode(mode);
    setShowLoginModal(true);
  };

  const onHome = pathname === '/';
  const onInsider = pathname.startsWith('/insider');
  const onCalendar = pathname.startsWith('/calendar');
  const onMarkets = pathname.startsWith('/markets');
  const onOptimiser = pathname.startsWith('/optimiser');
  const linkBase = 'hidden md:inline-flex items-center gap-1 text-sm font-medium transition-colors';
  const active = 'text-videbits-700';
  const idle = 'text-videbits-600 hover:text-videbits-800';

  // Premium nav links navigate for everyone; the gated pages themselves prompt
  // sign in (via ProtectedRoute) and follow through after login.
  const premiumLink = (to, isActive, children) => (
    <Link to={to} className={`${linkBase} ${isActive ? active : idle}`}>
      {children}
    </Link>
  );

  // Same nav as desktop, rendered as full-width rows in the mobile menu so every
  // feature is reachable on a phone. Premium rows prompt sign in when signed out.
  const mobileRow = 'flex items-center gap-2 px-4 py-3 text-sm font-medium border-t border-gray-100';
  const mobileItem = (to, isActive, children) => (
    <Link to={to} onClick={() => setMobileMenuOpen(false)} className={`${mobileRow} ${isActive ? active : idle}`}>
      {children}
    </Link>
  );

  return (
    <>
      <header className="border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 md:py-4 flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <button
              type="button"
              onClick={() => setMobileMenuOpen((o) => !o)}
              className="md:hidden inline-flex items-center justify-center w-9 h-9 -ml-1 text-videbits-600 hover:text-videbits-800"
              aria-label="Menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <Link to="/" className="flex items-center space-x-2 md:space-x-3 hover:opacity-80 transition-opacity cursor-pointer">
              <img src="/logo-transparent.svg" alt="VideBits Logo" className="w-8 h-8" />
              <h1 className="hidden md:block text-xl font-light text-gray-900">VideBits</h1>
            </Link>
            {/* Calendar is public (release calendar) so it's a normal link.
                Insider + Optimiser require login: shown to everyone for
                discoverability, but prompt sign in when signed out. */}
            <Link to="/" className={`${linkBase} ${onHome ? active : idle}`}>
              <Home className="w-4 h-4" />
              Home
            </Link>
            <Link to="/calendar" className={`${linkBase} ${onCalendar ? active : idle}`}>
              <CalendarDays className="w-4 h-4" />
              Calendar
            </Link>
            <Link to="/markets" className={`${linkBase} ${onMarkets ? active : idle}`}>
              <TrendingUp className="w-4 h-4" />
              Markets
            </Link>
            {premiumLink('/insider', onInsider, <><Zap className="w-4 h-4" />Insider</>)}
            {premiumLink('/optimiser', onOptimiser, <><Wallet className="w-4 h-4" />Optimiser</>)}
            {/* The SEO hubs, grouped rather than six more top-level links. */}
            <div className="relative hidden md:block" ref={browseRef}>
              <button
                type="button"
                onClick={() => setBrowseOpen((o) => !o)}
                aria-expanded={browseOpen}
                aria-haspopup="true"
                className={`inline-flex items-center gap-1 text-sm font-medium transition-colors ${browseOpen ? active : idle}`}
              >
                Browse
                <ChevronDown className={`w-4 h-4 transition-transform ${browseOpen ? 'rotate-180' : ''}`} />
              </button>
              {browseOpen && (
                <div className="absolute left-0 mt-2 w-52 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-20">
                  {BROWSE_HUBS.map((h) => {
                    const cls = 'block px-4 py-2 text-sm text-gray-700 hover:bg-videbits-50 hover:text-videbits-800';
                    // Native routes use <Link> (client-side); the /seo hubs are
                    // server-rendered pages and need a real navigation.
                    return h.spa ? (
                      <Link key={h.href} to={h.href} onClick={() => setBrowseOpen(false)} className={cls}>
                        {h.label}
                      </Link>
                    ) : (
                      <a key={h.href} href={h.href} className={cls}>{h.label}</a>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Authentication Section (mirrors the main app header) */}
          <div className="flex items-center space-x-2 md:space-x-4">
            {authLoading ? (
              <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse" />
            ) : isAuthenticated ? (
              <div className="flex items-center space-x-2 md:space-x-3">
                <NotificationBell />
                <UserDropdown />
              </div>
            ) : (
              <>
                <div className="hidden md:flex items-center space-x-4">
                  <button onClick={() => openLoginModal('login')} className="px-4 py-2 text-videbits-600 hover:text-videbits-800 transition-colors">
                    Sign In
                  </button>
                  <button onClick={() => openLoginModal('signup')} className="px-4 py-2 bg-videbits-600 text-white rounded-lg hover:bg-videbits-700 transition-colors">
                    Sign Up
                  </button>
                </div>
                <div className="md:hidden flex items-center space-x-2">
                  <button onClick={() => openLoginModal('login')} className="px-3 py-2 text-sm text-videbits-700 hover:text-videbits-900 transition-colors font-medium">
                    Sign In
                  </button>
                  <button onClick={() => openLoginModal('signup')} className="px-4 py-2 text-sm bg-videbits-600 text-white rounded-lg hover:bg-videbits-700 transition-colors font-medium">
                    Sign Up
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Mobile nav menu — same links as the desktop header */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white">
            {mobileItem('/', onHome, <><Home className="w-4 h-4" />Home</>)}
            {mobileItem('/calendar', onCalendar, <><CalendarDays className="w-4 h-4" />Calendar</>)}
            {mobileItem('/markets', onMarkets, <><TrendingUp className="w-4 h-4" />Markets</>)}
            {mobileItem('/insider', onInsider, <><Zap className="w-4 h-4" />Insider</>)}
            {mobileItem('/optimiser', onOptimiser, <><Wallet className="w-4 h-4" />Optimiser</>)}
            <div className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400 border-t border-gray-100">
              Browse
            </div>
            {BROWSE_HUBS.map((h) => (
              h.spa ? (
                <Link
                  key={h.href}
                  to={h.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`${mobileRow} ${idle}`}
                >
                  {h.label}
                </Link>
              ) : (
                <a
                  key={h.href}
                  href={h.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`${mobileRow} ${idle}`}
                >
                  {h.label}
                </a>
              )
            ))}
          </div>
        )}
      </header>

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        initialMode={loginModalMode}
      />
    </>
  );
}
